
import { Component, Vue } from 'vue-property-decorator';
import ChatRepositoryInterface, {
  ChatInterface,
  chatRateMap,
  ChatsWSMessageInterface,
  ChatWSMessageInterface,
  MessageRecipientsType,
} from '@/services/QA/chat/interfaces';
import Themeable from 'vuetify/lib/mixins/themeable';
import TagRepositoryInterface, { TagInterface } from '@/services/QA/tag/interfaces';
import ScoreTypeRepositoryInterface, { ScoreTypeInterface } from '@/services/QA/scoreType/interfaces';
import ScoreGradeRepositoryInterface, { ScoreGradeInterface } from '@/services/QA/scoreGrade/interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { ReviewChatForm } from '@/services/QA/chat/forms/ReviewChatForm';
import CommentRepositoryInterface, { CommentInterface } from '@/services/QA/comment/interfaces';
import NoteRepositoryInterface, { NoteInterface } from '@/services/QA/note/interfaces';
import ChatMessageImageDialog from '@/components/dialogs/qa/chat/ChatMessageImageDialog.vue';
import EscalateDialog from '@/components/dialogs/qa/escalation/EscalateDialog.vue';
import FeedbackDialog from '@/components/dialogs/qa/feedback/FeedbackDialog.vue';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import ConfirmChatReviewDialog from '@/components/dialogs/qa/chat/ConfirmChatReview.vue';
import { UserHasScopes } from '@/views/middleware/Route/Auth';
import { Roles } from '@/services/Roles';
import DeleteCommentDialog from '@/components/dialogs/qa/comment/DeleteCommentDialog.vue';
import DeleteNoteDialog from '@/components/dialogs/qa/note/DeleteNoteDialog.vue';
import EditCommentDialog from '@/components/dialogs/qa/comment/EditCommentDialog.vue';
import EditNoteDialog from '@/components/dialogs/qa/note/EditNoteDialog.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { DataTableHeader } from 'vuetify';
import LoadedChatFromQueueDialog from '@/components/dialogs/qa/chat/LoadedChatFromQueueDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ChatMessageImageDialog,
    EscalateDialog,
    FeedbackDialog,
    ConfirmChatReviewDialog,
    DeleteCommentDialog,
    DeleteNoteDialog,
    EditCommentDialog,
    EditNoteDialog,
    LoadedChatFromQueueDialog,
  },
  inject: [
    'userRepository',
    'chatRepository',
    'tagRepository',
    'scoreTypeRepository',
    'scoreGradeRepository',
    'commentRepository',
    'noteRepository',
    'profileRepository',
  ],
  sockets: {
    chatData(msg: ChatWSMessageInterface): void {
      this.$emit('showUnreviewedChat', msg.chat);
    },
    chatsData(msg: ChatsWSMessageInterface): void {
      this.$emit('updateUnfinishedReviews', msg.chats);
    },
  },
})
export default class ReviewChat extends Vue {
  private chat!: ChatInterface;
  private reviewSwitch: boolean = false;
  private loading: boolean = true;
  private noChatsForReview: boolean = false;
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};

  private readonly chatRepository!: ChatRepositoryInterface;
  private readonly userRepository!: UserRepoInterface;
  private readonly tagRepository!: TagRepositoryInterface;
  private readonly scoreTypeRepository!: ScoreTypeRepositoryInterface;
  private readonly scoreGradeRepository!: ScoreGradeRepositoryInterface;
  private readonly commentRepository!: CommentRepositoryInterface;
  private readonly noteRepository!: NoteRepositoryInterface;
  private readonly profileRepository!: ProfileRepositoryInterface;
  private scores: { [scoreTypeId: number]: number } = {};
  private valid: boolean = false;

  private tags: TagInterface[] = [];
  private tagsSelected: TagInterface[] = [];
  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[] = [];
  private comment: string = '';
  private messageComment: string = '';
  private privateNote: string = '';
  private form: WPXFormValue<ReviewChatForm> = new WPXFormValue(ReviewChatForm);
  private urlForDialog: string | false = false;
  private imageDialog: string = '';
  private confirmReviewChatDialog: string = '';
  private userHasScopes: object = UserHasScopes;
  private userRoles: object = Roles;

  private feedbackDialog: string = '';
  private escalateDialog: string | false = false;
  private searchTag: string = '';
  private chatRateMap: object = chatRateMap;
  private commentIdForDialog: number = 0;
  private deleteCommentDialog: string | false = false;
  private noteIdForDialog: number = 0;
  private deleteNoteDialog: string | false = false;
  private commentIdForEditDialog: number = 0;
  private commentContentForDialog: string = '';
  private editCommentDialog: string | false = false;
  private noteIdForEditDialog: number = 0;
  private noteContentForDialog: string = '';
  private editNoteDialog: string | false = false;
  private responseReceived: boolean = false;
  private loadedChatFromQueueDialog: string | false = false;

  private loadedFromQueue: boolean = false;


  private headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'External', value: 'external' },
    { text: 'Date & Time', value: 'endedTimestamp' },
    { text: 'Chat Rated', value: 'rate' },
    { text: 'Client Name', value: 'visitor.name' },
    { text: 'Client Logged', value: 'visitor.isLogged' },
    { text: 'Participants', value: 'agents' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];
  private unfinishedReviews: ChatInterface[] = [];
  private tableLoading: boolean = true;
  private tableResponseReceived: boolean = false;
  private chatRate: { '0': string, '1': string, '2': string } = chatRateMap;

  public async beforeCreate(): Promise<void> {
    this.$on('showUnreviewedChat', async (chat: ChatInterface) => {
      await this.mergeChatProperties(chat);
    });
    this.$on('updateUnfinishedReviews', async (chats: ChatInterface[]) => {
      await this.assignUnfinishedReviewsData(chats);
    });
  }

  public async created(): Promise<void> {
    await this.initialize();
    const chatId = this.$route.params.chatId;
    if (chatId) {
      this.loadedFromQueue = true;
      this.reviewSwitch = true;
      await this.chatsReviewSwitch(this.reviewSwitch, chatId);
      this.reviewSwitch = false;
    } else {
      await this.chatsReviewSwitch(this.reviewSwitch);
    }
  }

  private async initialize(): Promise<void> {
    this.tags = await this.tagRepository.getActiveTags();
    this.scoreTypes = await this.scoreTypeRepository.getAllScoreTypes();
    this.scoreGrades = await this.scoreGradeRepository.getAllScoreGrades();
  }

  private addScore(scoreTypeId: number, scoreGradeId: number): void {
    this.scores[scoreTypeId] = scoreGradeId;
    this.valid = Object.keys(this.scores).length === this.scoreTypes.length;
  }

  private removeScore(scoreTypeId: number): void {
    delete this.scores[scoreTypeId];
    this.valid = Object.keys(this.scores).length === this.scoreTypes.length;
  }

  private openReviewChatDialog(): void {
    this.confirmReviewChatDialog = 'confirm-chat-review-dialog';
  }

  private async prepareCommentForEdit(commentId: number): Promise<void> {
    let commentIndex = this.chat.comments.findIndex((comment) => comment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.commentIdForEditDialog = this.chat.comments[commentIndex].comment.id;
      this.commentContentForDialog = this.chat.comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    } else {
      let messageIndex = -1;

      this.chat.messages.forEach((message, mi) => {
        message.comments.forEach((messageComment, ci) => {
          if (messageComment.comment.id === commentId) {
            messageIndex = mi;
            commentIndex = ci;
          }
        });
      });

      this.commentIdForEditDialog = this.chat.messages[messageIndex].comments[commentIndex].comment.id;
      this.commentContentForDialog = this.chat.messages[messageIndex].comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    }
  }

  private async editComment(commentId: number, content: string): Promise<void> {
    let commentIndex = this.chat.comments.findIndex((comment) => comment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.chat.comments[commentIndex].comment.content = content;
      this.chat.comments[commentIndex].comment.updatedAt = Math.floor(Date.now() / 1000);
    } else {
      let messageIndex = -1;

      this.chat.messages.forEach((message, mi) => {
        message.comments.forEach((messageComment, ci) => {
          if (messageComment.comment.id === commentId) {
            messageIndex = mi;
            commentIndex = ci;
          }
        });
      });

      this.chat.messages[messageIndex].comments[commentIndex].comment.content = content;
      this.chat.messages[messageIndex].comments[commentIndex].comment.updatedAt = Math.floor(Date.now() / 1000);
    }
  }

  private async deleteComment(commentId: number): Promise<void> {
    this.commentIdForDialog = commentId;
    this.deleteCommentDialog = 'delete-comment-dialog';
  }

  private async prepareNoteForEdit(noteId: number): Promise<void> {
    const noteIndex = this.chat.notes.findIndex((chatNote) => chatNote.note.id === noteId);
    if (noteIndex !== -1) {
      this.noteIdForEditDialog = this.chat.notes[noteIndex].note.id;
      this.noteContentForDialog = this.chat.notes[noteIndex].note.content;
      this.editNoteDialog = 'edit-note-dialog';
    }
  }

  private async editNote(noteId: number, content: string): Promise<void> {
    const noteIndex = this.chat.notes.findIndex((chatNote) => chatNote.note.id === noteId);
    if (noteIndex !== -1) {
      this.chat.notes[noteIndex].note.content = content;
      this.chat.notes[noteIndex].note.updatedAt = Math.floor(Date.now() / 1000);
    }
  }

  private async deleteNote(noteId: number): Promise<void> {
    this.noteIdForDialog = noteId;
    this.deleteNoteDialog = 'delete-note-dialog';
  }

  private removeNote(noteId: number): void {
    const noteIndex = this.chat.notes.findIndex((chatNote) => chatNote.note.id === noteId);

    if (noteIndex !== -1) {
      delete this.chat.notes[noteIndex];
      this.chat.notes = this.chat.notes.filter((note) => !!note);
    }

    this.$forceUpdate();
  }

  private removeComment(commentId: number): void {
    let commentIndex = this.chat.comments.findIndex((chatComment) => chatComment.comment.id === commentId);

    if (commentIndex !== -1) {
      delete this.chat.comments[commentIndex];
      this.chat.comments = this.chat.comments.filter((comment) => !!comment);
    } else {
      let messageIndex = -1;
      this.chat.messages.forEach((message, mi) => {
        message.comments.forEach((messageComment, ci) => {
          if (messageComment.comment.id === commentId) {
            messageIndex = mi;
            commentIndex = ci;
          }
        });

      });

      delete this.chat.messages[messageIndex].comments[commentIndex];
      this.chat.messages[messageIndex].comments = this.chat.messages[messageIndex]
        .comments.filter((comment) => !!comment);
    }

    this.$forceUpdate();
  }

  private async reviewChat(sendStats: boolean = true, reviewLater: boolean = false): Promise<void> {
    try {
      await this.chatRepository.reviewChat(
        this.chat.id,
        this.chat.agents.filter((agent) => !!agent.id).map((agent) => String(agent.id)),
        this.scores,
        this.tagsSelected.map((tag) => tag.id),
        sendStats,
        reviewLater,
      );

      if (sendStats) {
        this.$successDialog.show('Chat Reviewed Successfully!');
      }

      if (this.loadedFromQueue) {
        await this.$router.push({
          name: 'chats_queue',
          params: {
            agentEmail: this.$route.params.agentEmail,
            page: this.$route.params.page,
            totalItems: this.$route.params.totalItems,
            reviewedIdsMap: this.$route.params.reviewedIdsMap,
            sortBy: this.$route.params.sortBy,
            sortDesc: this.$route.params.sortDesc,
          },
        });
        return;
      }

      this.chat = {} as ChatInterface;
      await this.chatsReviewSwitch(this.reviewSwitch);

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async addComment(): Promise<void> {
    try {
      const commentDTO: Partial<CommentInterface> = {
        content: this.comment,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      const comment = await this.commentRepository.addChatComment(this.chat.id, commentDTO);
      if (!this.chat.comments) {
        this.chat.comments = [];
      }

      if (!this.usersMap[this.$store.getters.id]) {
        this.usersMap[this.$store.getters.id] = {
          id: this.$store.getters.id,
          email: this.$store.getters.email,
        };
      }

      this.chat.comments.push(comment);
      this.comment = '';

    } catch (err) {
      console.log(err);
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async addNote(): Promise<void> {
    try {
      const noteDTO: Partial<NoteInterface> = {
        content: this.privateNote,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      const note = await this.noteRepository.addChatNote(this.chat.id, noteDTO);

      if (!this.usersMap[this.$store.getters.id]) {
        this.usersMap[this.$store.getters.id] = {
          id: this.$store.getters.id,
          email: this.$store.getters.email,
        };
      }

      this.chat.notes.push(note);
      this.privateNote = '';

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async addCommentToMessage(messageId: number): Promise<void> {
    try {
      const commentDTO: Partial<CommentInterface> = {
        content: this.messageComment,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      const comment = await this.commentRepository.addMessageComment(this.chat.id, messageId, commentDTO);

      if (!this.usersMap[this.$store.getters.id]) {
        this.usersMap[this.$store.getters.id] = {
          id: this.$store.getters.id,
          email: this.$store.getters.email,
        };
      }

      const index = this.chat.messages.findIndex((message) => message.id === messageId);
      this.chat.messages[index].comments.push(comment);
      this.messageComment = '';

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async chatsReviewSwitch(_: boolean, chatId?: string): Promise<void> {
    if (!this.chat?.id) {
      await this.loadUnfinishedReviews();
      this.loading = true;
    }

    if (!this.reviewSwitch || (this.reviewSwitch && this.chat?.id)) {
      return;
    }

    this.tableLoading = true;
    this.tableResponseReceived = false;

    this.valid = false;
    this.responseReceived = false;
    this.tagsSelected = [];
    this.scores = {};
    this.chat = await this.chatRepository.getLastUnreviewedChat(chatId);
    this.responseReceived = true;

    if (chatId && chatId !== this.chat.id) {
      this.loadedChatFromQueueDialog = 'loaded-chat-from-queue-dialog';
    }

    if (!this.chat) {
      this.reviewSwitch = false;
      this.tableLoading = true;
      this.tableResponseReceived = false;
      await this.loadUnfinishedReviews();
      this.$successDialog.show('No More Chats For Review!');

      return;
    }

    if (this.chat.scores.length > 0) {
      this.parseScores();
    }

    await this.getUsersInfo();

    this.chat.messages = this.chat.messages.map((message) => {
      message.commentInputVisible = false;
      return message;
    });
  }

  private parseScores(): void {
    this.chat.scores.forEach((chatScore) => {
      this.addScore(chatScore.score.type.id, chatScore.score.grade.id);
    });
  }

  private async loadUnfinishedReviews(): Promise<void> {
    if (this.reviewSwitch) {
      return;
    }
    await this.chatRepository.getUnfinishedReviews();
    this.tableResponseReceived = true;
  }

  private async assignUnfinishedReviewsData(chats: ChatInterface[]): Promise<void> {
    if (!this.tableResponseReceived) {
      setTimeout(() => {
        this.assignUnfinishedReviewsData(chats);
      }, 100);

      return;
    }

    if (!this.tableLoading) {
      return;
    }

    this.unfinishedReviews = chats.sort((chatA, chatB) => chatB.endedTimestamp - chatA.endedTimestamp);
    this.tableLoading = false;
  }

  private async selfAssign(): Promise<void> {
    await this.reviewChat(false, true);
  }

  private async finishReview(chat: ChatInterface): Promise<void> {
    this.reviewSwitch = true;
    await this.chatsReviewSwitch(this.reviewSwitch, chat.id);
    this.reviewSwitch = false;
  }

  private showCommentArea(messageId: number): void {
    const index = this.chat.messages.findIndex((message) => message.id === messageId);
    this.chat.messages.forEach((message, i) => {
      if (i !== index) {
        message.commentInputVisible = false;
      }
    });
    this.chat.messages[index].commentInputVisible = !this.chat.messages[index].commentInputVisible;
    this.$forceUpdate();
  }

  private async createTag(): Promise<void> {
    if (!this.searchTag) {
      return;
    }

    const newTag = await this.tagRepository.createTag({
      label: this.searchTag,
      color: '#808080FF',
    });

    this.tags.push(newTag);
    // @ts-ignore
    this.tagsSelected.push(newTag);
  }

  private escalate(): void {
    this.escalateDialog = 'escalate-dialog';
  }

  private feedback(): void {
    this.feedbackDialog = 'feedback-dialog';
  }

  private async closeReview(): Promise<void> {
    await this.$router.push('queue');
  }

  private showImage(url: string): void {
    this.imageDialog = 'chat-message-image-dialog';
    this.urlForDialog = url;
  }

  private async mergeChatProperties(chatData: ChatInterface): Promise<void> {
    if (!this.responseReceived) {
      setTimeout(async () => {
        await this.mergeChatProperties(chatData);
      }, 100);

      return;
    }

    if (this.chat.id !== chatData.id) {
      return;
    }

    this.chat.startedTimestamp = chatData.startedTimestamp;
    this.chat.endedTimestamp = chatData.endedTimestamp;
    this.chat.visitor = chatData.visitor;
    this.chat.agents = chatData.agents;
    this.chat.supervisors = chatData.supervisors;
    this.chat.rate = chatData.rate;
    this.chat.messages.map((message) => {
      const index = chatData.messages?.findIndex((msg) => {
        return msg.id === message.id;
      });

      return Object.assign(message, chatData.messages[index]);
    });

    this.chat.messages.map((message) => {
      message.isWhisper = message.recipients === MessageRecipientsType.AGENTS;

      return message;
    });

    this.chat.messages.sort((messageA, messageB) => messageA.timestamp - messageB.timestamp);
    const users = await this.userRepository.getUsersByEmails([
      ...this.chat.agents.map((agent) => agent.login),
      ...this.chat.supervisors.map((supervisor) => supervisor.login),
    ]);

    const usersProfiles = await this.profileRepository
      .getBaseProfileDataByUsersIds(users.map((userInfo) => String(userInfo.id)));

    users.forEach((userInfo) => {
      const profileIndex = usersProfiles.findIndex((profile) => profile.userId === String(userInfo.id));

      if (profileIndex === -1) {
        return;
      }

      const agentIndex = this.chat.agents.findIndex((agent) => agent.login === userInfo.email);
      if (agentIndex !== -1) {
        this.chat.agents[agentIndex].id = userInfo.id;
        this.chat.agents[agentIndex].fullName =
          `${usersProfiles[profileIndex].firstName} ${usersProfiles[profileIndex].lastName}`;
      }

      const supervisorIndex = this.chat.supervisors.findIndex((supervisor) => supervisor.login === userInfo.email);
      if (supervisorIndex !== -1) {
        this.chat.supervisors[supervisorIndex].id = userInfo.id;
        this.chat.supervisors[supervisorIndex].fullName =
          `${usersProfiles[profileIndex].firstName} ${usersProfiles[profileIndex].lastName}`;
      }

    });

    this.loading = false;
    this.tableLoading = false;
  }

  private async getUsersInfo(): Promise<void> {
    const usersIds: string[] = [];
    this.chat.comments.forEach((chatComment) => {
      if (usersIds.indexOf(chatComment.comment.authorId) === -1) {
        usersIds.push(chatComment.comment.authorId);
      }
    });

    this.chat.notes.forEach((chatNote) => {
      if (usersIds.indexOf(chatNote.note.authorId) === -1) {
        usersIds.push(chatNote.note.authorId);
      }
    });

    this.chat.messages.forEach((message) => {
      message.comments.forEach((messageComment) => {
        if (usersIds.indexOf(messageComment.comment.authorId) === -1) {
          usersIds.push(messageComment.comment.authorId);
        }
      });
    });

    if (!usersIds.length) {
      return;
    }

    const users = await this.userRepository.getUsersByIds(usersIds);
    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });
  }
}
